import type React from "react";
import { createContext, useContext, useEffect } from "react";

import * as Sentry from "@sentry/react";
import { graphql, useFragment } from "react-relay";

import type {
  UserContext_platform$key,
  UserContext_platform$data,
  Permission as RoleScopePermission,
  CloudCostType,
} from "./__generated__/UserContext_platform.graphql";

interface UserContextType {
  roleScopes: UserContext_platform$data["platform"]["roleScopes"];
  costReportingConfig: UserContext_platform$data["platform"]["costReportingConfig"];
  currentUser: UserContext_platform$data["whoami"]["user"];
}

interface Props {
  children: React.ReactNode;
  query: UserContext_platform$key;
}

export type { RoleScopePermission as Permission, CloudCostType };

export const UserContext = createContext<UserContextType | undefined>(
  undefined,
);

export const UserContextProvider = ({ children, query }: Props) => {
  const data = useFragment(
    graphql`
      fragment UserContext_platform on Query {
        platform {
          costReportingConfig {
            awsCostType
            azureCostType
            gcpCostType
          }
          roleScopes {
            accountGroup {
              userPermissions
            }
            policyCollection {
              userPermissions
            }
            repository {
              userPermissions
            }
            system {
              userPermissions
            }
          }
        }
        whoami {
          email
          displayName
          user {
            ssoUser
          }
        }
      }
    `,
    query,
  );

  let roleScopes: UserContextType["roleScopes"] = {
    accountGroup: { userPermissions: [] },
    policyCollection: { userPermissions: [] },
    repository: { userPermissions: [] },
    system: { userPermissions: [] },
  };

  let costReportingConfig: UserContextType["costReportingConfig"] = {
    awsCostType: "EffectiveCost",
    azureCostType: "BilledCost",
    gcpCostType: "EffectiveCost",
  };

  if (data?.platform?.roleScopes) {
    const sourceScopes = data.platform.roleScopes;
    roleScopes = {
      accountGroup: {
        userPermissions: sourceScopes.accountGroup?.userPermissions || [],
      },
      policyCollection: {
        userPermissions: sourceScopes.policyCollection?.userPermissions || [],
      },
      repository: {
        userPermissions: sourceScopes.repository?.userPermissions || [],
      },
      system: {
        userPermissions: sourceScopes.system?.userPermissions || [],
      },
    };
  }

  if (data?.platform?.costReportingConfig) {
    const sourceCostConfig = data.platform.costReportingConfig;
    costReportingConfig = {
      awsCostType: sourceCostConfig.awsCostType,
      azureCostType: sourceCostConfig.azureCostType,
      gcpCostType: sourceCostConfig.gcpCostType,
    };
  }

  // Properties are manually mapped to avoid unused fields relay linter errors
  // Properties are not used in the component, but are exposed to child components used at the start of the app
  // More properties can be added here if needed
  const currentUser = { ssoUser: data.whoami.user.ssoUser };

  useEffect(() => {
    Sentry.setUser({
      email: data.whoami.email ?? undefined,
      username: data.whoami.displayName ?? undefined,
    });
    return () => {
      Sentry.setUser(null);
    };
  }, [data.whoami.email, data.whoami.displayName]);

  return (
    <UserContext.Provider
      value={{ roleScopes, costReportingConfig, currentUser }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUserContext must be used within a UserContextProvider");
  }
  return context;
};
